import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ObjectUtils, { coalesce, normalizeQuillLists, sanitize } from "../../../../utils/ObjectUtils";
import { findAnswer } from "../../../../utils/QuestionUtils";

import { faAngleLeft, faAngleRight, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import findIndex from "lodash/findIndex";
import PropTypes from "prop-types";
import { Badge, ButtonGroup, ButtonToolbar } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import FormGroup from "react-bootstrap/FormGroup";
import FormLabel from "react-bootstrap/FormLabel";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import { ConfirmModal } from "../../../../common/confirmModal";
import { MEASUREMENT_COL_KEY } from "../../../../utils/FilterUtil";
import { convertActivityMonitoringMapAnswer } from "./ActivityMonitoringQuestion";
import QuestionValidation from "./QuestionValidation";
import ActivityListRenderer from "./renderer/ActivityListRenderer";
import CheckboxRenderer from "./renderer/CheckboxRenderer";
import DateRenderer from "./renderer/DateRenderer";
import RadioRenderer from "./renderer/RadioRenderer";
import TextRenderer from "./renderer/TextRenderer";

const { findByTemplate, getDeepProperty } = ObjectUtils;

// FIXME: re-write state management

const CHANGE_TYPE_NEW = "ADD";
const CHANGE_TYPE_SET = "SET";
const CHANGE_TYPE_REMOVE = "REMOVE";

const TableQuestionDialog = ({
  open,
  onClose,
  question,
  surveyTemplate,
  rows,
  rowIndex,
  questionAnswer,
  validation,
  addRow,
  deleteRows,
  isNewRow,
  setQuestionAnswer,
  monitoringEditOnly,
  surveyResult,
  addQuestionAnswer,
  removeQuestionAnswer,
  validateQuestionAnswer,
  wideScreen
}) => {
  const intl = useIntl();
  const [index, setIndex] = useState(rowIndex);
  const row = index > -1 ? rows[index] : undefined;
  const rowName = row ? row.name : undefined;

  const questionAnswers = coalesce(questionAnswer.answers, []);
  const rowAnswers = rowName ? questionAnswers.filter((rowAnswer) => rowAnswer.label === rowName) : [];
  const isValid = !validation || validation.filter((val) => !val.isOk).length === 0;
  const [answers, setAnswers] = useState(rowName ? { [rowName]: [...rowAnswers] } : {});
  const [changeSet, setChangeSet] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [measurementFilter, setMeasurementFilter] = useState(null);

    useEffect(() => {
    if (isNewRow) {
      setChangeSet([
        {
          type: CHANGE_TYPE_NEW,
          answer: {
            label: `dynamic${index + 1}`,
          }
        }
      ]);
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  useEffect(() => {
    setIndex((idx) => (rowIndex !== idx ? rowIndex : idx));
  }, [rowIndex]);

  useEffect(() => {
    setAnswers((answers) => {
      if (rowName && !answers[row.name]) {
        const rowAnswers = row ? questionAnswers.filter((rowAnswer) => rowAnswer.label === rowName) : [];

        return {
          ...answers,
          [rowName]: rowAnswers
        };
      }
      return answers;
    });
  }, [index, questionAnswers, row, rowName]);

  const handleCancel = () => {
    if (changeSet.length > 0) {
      setShowConfirmModal(true);
      return;
    }

    onClose();
  };

  const close = () => {
    const keys = changeSet.filter(change => change.type === CHANGE_TYPE_NEW).map(change => change.answer.label);
    if (keys.length) {
      deleteRows?.(question, keys);
    }

    onClose();
    setChangeSet([]);
  };

  const handleOpenPrev = () => {
    if (index > 0) {
      setIndex(index - 1);
    }
  };

  const handleOpenNext = () => {
    if (index < rows.length - 1) {
      setIndex(index + 1);
    }
  };

  const handleOpenNew = () => {
    addRow();
    setChangeSet((changeSet) => [
      ...changeSet,
      {
        type: CHANGE_TYPE_NEW,
        answer: {
          label: `dynamic${index + 2}`,
        }
      }
    ]);
    setIndex(index + 1);
  };

  const handleApply = () => {
    onClose();

    changeSet.forEach((change) => {
      switch (change.type) {
        case CHANGE_TYPE_NEW:
          if (!isNewRow) {
            addQuestionAnswer(question, change.answer);
          }
          break;
        case CHANGE_TYPE_SET:
          setQuestionAnswer(question, change.answer);
          break;
        case CHANGE_TYPE_REMOVE:
          removeQuestionAnswer(question, change.answer);
          break;
        default:
          break;
      }
    });
  };

  const handleAddAnswer = (question, answer) => {
    setAnswers((answers) => {
      const newAnswers = [...answers[rowName]];
      newAnswers.push(answer);

      return {
        ...answers,
        [rowName]: newAnswers
      };
    });

    setChangeSet((changeSet) => [
      ...changeSet,
      {
        type: CHANGE_TYPE_NEW,
        answer
      }
    ]);
  };

  const handleSetAnswer = (question, answer, ignoreName = true) => {
    validateQuestionAnswer(question, answer);
    setAnswers((answers) => {
      const newAnswers = [...answers[rowName]];

      const predicate = {
        colLabel: answer.colLabel,
        label: answer.label
      };
      if (!ignoreName && answer.name) {
        predicate.name = answer.name;
      }

      const index = findIndex(newAnswers, predicate);

      if (index === -1) {
        newAnswers.push(answer);
      } else {
        newAnswers.splice(index, 1, answer);
      }

      return {
        ...answers,
        [rowName]: newAnswers
      };
    });

    setChangeSet((changeSet) => [
      ...changeSet,
      {
        type: CHANGE_TYPE_SET,
        answer
      }
    ]);
  };

  const handleRemoveAnswer = (question, answer) => {
    setAnswers((answers) => {
      const newAnswers = [...answers[rowName]];

      const predicate = {
        colLabel: answer.colLabel,
        label: answer.label
      };
      if (answer.name) {
        predicate.name = answer.name;
      }

      const index = findIndex(newAnswers, predicate);

      if (index !== -1) {
        newAnswers.splice(index, 1);

        return {
          ...answers,
          [rowName]: newAnswers
        };
      }
    });

    setChangeSet((changeSet) => [
      ...changeSet,
      {
        type: CHANGE_TYPE_REMOVE,
        answer
      }
    ]);
  };

  const renderColumn = (column, isIncomplete) => {
    const qValidators = findByTemplate(coalesce(getDeepProperty(question, "validators"), []), {
      columnName: column.name
    });
    const qValidations = row
      ? findByTemplate(coalesce(validation, []), {
          columnName: column.name,
          rowName: row.name
        })
      : [];
    const validator = getDeepProperty(qValidators, 0);
    const qValidation = getDeepProperty(qValidations, 0);
    // check if we are in monitoring-only mode - if yes only allow monitoring field to be filled out
    const disabled = monitoringEditOnly ? !column.unchangeable : false;

    switch (column.type) {
      case "Text":
        return (
          <>
            <TextRenderer
              isIncomplete={isIncomplete}
              row={row}
              rows={3}
              column={column}
              disabled={disabled}
              question={question}
              collection={answers[rowName]}
              setQuestionAnswer={handleSetAnswer}
              className={wideScreen ? "monitoring" : ""}
              showLengthValidation
            />
          </>
        );

      case "Number":
        return (
          <>
            <TextRenderer
              row={row}
              column={column}
              disabled={disabled}
              question={question}
              collection={answers[rowName]}
              setQuestionAnswer={handleSetAnswer}
              isIncomplete={isIncomplete}
            />

            <QuestionValidation
              showOnlyError
              validation={qValidation}
              validator={validator}
              questionType={column.type.toLowerCase()}
            />
          </>
        );

      case "Date":
        return (
          <DateRenderer
            row={row}
            column={column}
            disabled={disabled}
            question={question}
            collection={answers[rowName]}
            setQuestionAnswer={handleSetAnswer}
          />
        );

      case "Radio":
      case "Dropdown":
      case "predefinedList":
        if (column.key === MEASUREMENT_COL_KEY) {
          return (
            <RadioRenderer
              row={row}
              column={column}
              disabled
              question={question}
              collection={answers[rowName]}
              setQuestionAnswer={handleSetAnswer}
              removeQuestionAnswer={handleRemoveAnswer}
              showOnlySelected={monitoringEditOnly}
              onFilter={setMeasurementFilter}
              isIncomplete={isIncomplete}
            />
          );
        }
        return (
          <RadioRenderer
            row={row}
            column={column}
            disabled={disabled}
            question={question}
            collection={answers[rowName]}
            setQuestionAnswer={handleSetAnswer}
            removeQuestionAnswer={handleRemoveAnswer}
            showOnlySelected={monitoringEditOnly}
            isIncomplete={isIncomplete}
          />
        );

      case "Checkbox":
        return (
          <CheckboxRenderer
            defaultStyle
            row={row}
            column={column}
            disabled={disabled}
            question={question}
            collection={answers[rowName]}
            addQuestionAnswer={handleAddAnswer}
            setQuestionAnswer={handleSetAnswer}
            removeQuestionAnswer={handleRemoveAnswer}
            showOnlyChecked={monitoringEditOnly}
          />
        );
      case "activityList":
        const questionAnswer = convertActivityMonitoringMapAnswer(surveyResult?.activityMonitoring);
        return (
          <ActivityListRenderer
            surveyTemplate={surveyTemplate}
            questionAnswer={questionAnswer}
            measurementFilter={measurementFilter}
          />
        );

      default:
        return null;
    }
  };

  const modalCssClass = wideScreen ? "table-question-dialog table-question-dialog-widescreen" : "table-question-dialog";

  const bodyRef = useRef();

  useEffect(() => {
    bodyRef.current?.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth"
    });
  }, [index]);

  function getDraftRows() {
    return Array.from(new Set(changeSet.map((change) => change.answer.label.replace("dynamic", ""))));
  }

  return (
    <>
      <Modal show={open} autoFocus enforceFocus size="xl" onHide={handleCancel} className={modalCssClass} scrollable>
        <Modal.Header closeButton>
          <Modal.Title dangerouslySetInnerHTML={sanitize(question.title)} />

        </Modal.Header>
        <Modal.Body ref={bodyRef}>
          <Container fluid>
            {changeSet.find((change) => Number(change.answer.label.replace("dynamic", "")) === index + 1) && (
              <Badge pill bg="warning" text="dark" className="mb-2">
                {intl.formatMessage({id: "templateList.status.draft"})}
              </Badge>
            )}

            <div
              className="rich-text-content"
              dangerouslySetInnerHTML={{ __html: normalizeQuillLists(question.description) }}
            />
            {question.columns.map((column, i) => {
              const answer = findAnswer(answers[rowName], { row, column });
              const isIncomplete = column.required && (!answer || !answer.value || answer.value.length === 0);
              return (
              <Row key={i}>
                <Col className={`${monitoringEditOnly && !column.unchangeable ? "disabled" : ""}`}>
                  <FormGroup  className="mb-3">
                    <FormLabel className={`text-primary fw-bold ${isIncomplete ? "text-danger" : ""}`}>
                      {column.text}
                      {column.required && " *"}
                    </FormLabel>
                    {column.infoText && (
                      <p>
                        <small>{column.infoText}</small>
                      </p>
                    )}
                    {renderColumn(column, isIncomplete)}
                  </FormGroup>
                </Col>
              </Row>
            )
            })}
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <ButtonToolbar className="flex-center-between gap-lg w-100">
            <ButtonGroup>
              <Button
                variant="secondary"
                disabled={index <= 0}
                onClick={handleOpenPrev}
                title={intl.formatMessage({ id: "table.dialog.previousRow" })}
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </Button>
              {index >= rows.length - 1 ? (
                <Button
                  onClick={handleOpenNew}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  <FormattedMessage id={"user.surveyView.table.addRow"} />
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={handleOpenNext}
                  title={intl.formatMessage({ id: "table.dialog.nextRow" })}
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </Button>
              )}
            </ButtonGroup>
            <big className="mx-auto">
              {`${intl.formatMessage({ id: "user.surveyView.type.row" })} ${index + 1} / ${rows.length}`}
            </big>
            <Button variant="secondary" onClick={handleCancel}>
              <FormattedMessage id={"app.common.Cancel"} />
            </Button>

            <Button onClick={handleApply} disabled={!isValid}>
              <FormattedMessage id={"app.common.action.save"} />
            </Button>
          </ButtonToolbar>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        title="app.common.unsaved_changes"
        intl={intl}
        message={intl.formatMessage({id: "dialog.leave.message.with.details"}, {rows: getDraftRows().sort().join(", ")})}
        onConfirm={close}
        handleClose={() => setShowConfirmModal(false)}
        show={showConfirmModal}
        cancelTextKey="confirmDialog.dismissLabel"
      />
    </>
  );
};

TableQuestionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  rows: PropTypes.array.isRequired,
  rowIndex: PropTypes.number.isRequired,
  question: PropTypes.object.isRequired,
  surveyTemplate: PropTypes.object,
  surveyResult: PropTypes.object,
  questionAnswer: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  addRow: PropTypes.func.isRequired,
  deleteRow: PropTypes.func.isRequired,
  deleteRows: PropTypes.func,
  addQuestionAnswer: PropTypes.func.isRequired,
  setQuestionAnswer: PropTypes.func.isRequired,
  removeQuestionAnswer: PropTypes.func.isRequired,
  validation: PropTypes.array,
  intl: PropTypes.object,
  validateQuestionAnswer: PropTypes.func,
  wideScreen: PropTypes.bool
};

export default TableQuestionDialog;
